import { useEffect } from 'react'
import { router, Link } from '@inertiajs/react'
import { AdminLayout } from '../../../layouts/admin-layout'
import { AppHeader } from '../../../components/app-header'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../../components/ui/card'
import { Badge } from '../../../components/ui/badge'
import { Button } from '../../../components/ui/button'
import { Progress } from '../../../components/ui/progress'

interface PoolRollout {
  id: number
  name: string
  target_count: number
  created_count: number
  failed_count: number
  status: string
  instances_per_minute: number
  source_repo_name: string
  started_at: string | null
  paused_at: string | null
  completed_at: string | null
  error_log: string | null
  created_at: string
  updated_at: string
  progress_percentage: number
  estimated_completion_at: string | null
  estimated_hours: number
  remaining_count: number
  effective_rate: number | null
}

interface AdminPoolRolloutShowProps {
  rollout: PoolRollout
  auth: {
    user: {
      id: number
      name: string
      email: string
      admin: boolean
      super_admin?: boolean
    }
  }
  flash?: {
    success?: string
    error?: string
  }
}

function Show({ rollout }: AdminPoolRolloutShowProps) {
  // Auto-refresh every 10 seconds if running
  useEffect(() => {
    if (rollout.status === 'running') {
      const interval = setInterval(() => {
        router.reload({ only: ['rollout'] })
      }, 10000)
      return () => clearInterval(interval)
    }
  }, [rollout.status])

  const getStatusBadge = (status: string) => {
    const statusVariants: Record<string, "success" | "warning" | "info" | "destructive" | "secondary"> = {
      'pending': 'secondary',
      'running': 'info',
      'paused': 'warning',
      'completed': 'success',
      'cancelled': 'destructive',
    }

    return (
      <Badge variant={statusVariants[status] || 'secondary'} className="text-sm px-3 py-1">
        {status.toUpperCase()}
      </Badge>
    )
  }

  const formatDateTime = (dateStr: string | null) => {
    if (!dateStr) return '-'
    return new Date(dateStr).toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    })
  }

  const handleStart = () => {
    if (confirm(`Start rollout "${rollout.name}"? This will begin creating ${rollout.instances_per_minute} instances per minute.`)) {
      router.post(`/admin/pool_rollouts/${rollout.id}/start`)
    }
  }

  const handlePause = () => {
    if (confirm('Pause this rollout? You can resume it later.')) {
      router.post(`/admin/pool_rollouts/${rollout.id}/pause`)
    }
  }

  const handleResume = () => {
    if (confirm('Resume this rollout?')) {
      router.post(`/admin/pool_rollouts/${rollout.id}/resume`)
    }
  }

  const handleCancel = () => {
    if (confirm('Cancel this rollout? This cannot be undone.')) {
      router.post(`/admin/pool_rollouts/${rollout.id}/cancel`)
    }
  }

  const handleDelete = () => {
    if (confirm(`Delete rollout "${rollout.name}"? This cannot be undone.`)) {
      router.delete(`/admin/pool_rollouts/${rollout.id}`)
    }
  }

  return (
    <>
      <AppHeader breadcrumbs={[{ label: 'Admin', href: '/admin' }, { label: 'Pool Rollouts', href: '/admin/pool_rollouts' }, { label: rollout.name }]} />

      <div className="flex flex-1 flex-col">
        <div className="@container/main flex flex-1 flex-col gap-2">
          <div className="flex flex-col gap-4 py-4 md:gap-6 md:py-6">
            <div className="px-4 lg:px-6">
              {/* Page Header */}
              <div className="mb-6 flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div>
                    <h1 className="text-3xl font-bold text-foreground">{rollout.name}</h1>
                    <p className="mt-2 text-sm text-muted-foreground">
                      Pool rollout details
                    </p>
                  </div>
                  {getStatusBadge(rollout.status)}
                </div>
                <Button variant="outline" asChild>
                  <Link href="/admin/pool_rollouts">Back to Rollouts</Link>
                </Button>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* Main Content */}
                <div className="lg:col-span-2 space-y-6">
                  {/* Progress Card */}
                  <Card className="shadow-none">
                    <CardHeader>
                      <CardTitle>Progress</CardTitle>
                      <CardDescription>
                        {rollout.created_count.toLocaleString()} of {rollout.target_count.toLocaleString()} instances created
                      </CardDescription>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-6">
                        {/* Large Progress Display */}
                        <div className="text-center py-4">
                          <div className="text-6xl font-bold text-foreground mb-2">
                            {rollout.progress_percentage}%
                          </div>
                          <Progress value={rollout.progress_percentage} className="h-4" />
                        </div>

                        {/* Stats Grid */}
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 pt-4 border-t border-border">
                          <div className="text-center">
                            <div className="text-2xl font-bold text-green-600">
                              {rollout.created_count.toLocaleString()}
                            </div>
                            <p className="text-sm text-muted-foreground">Created</p>
                          </div>
                          <div className="text-center">
                            <div className="text-2xl font-bold text-red-600">
                              {rollout.failed_count}
                            </div>
                            <p className="text-sm text-muted-foreground">Failed</p>
                          </div>
                          <div className="text-center">
                            <div className="text-2xl font-bold text-blue-600">
                              {rollout.remaining_count.toLocaleString()}
                            </div>
                            <p className="text-sm text-muted-foreground">Remaining</p>
                          </div>
                          <div className="text-center">
                            <div className="text-2xl font-bold text-foreground">
                              {rollout.target_count.toLocaleString()}
                            </div>
                            <p className="text-sm text-muted-foreground">Target</p>
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>

                  {/* Configuration Card */}
                  <Card className="shadow-none">
                    <CardHeader>
                      <CardTitle>Configuration</CardTitle>
                      <CardDescription>Rollout settings</CardDescription>
                    </CardHeader>
                    <CardContent>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <p className="text-sm font-medium text-muted-foreground">Rate</p>
                          <p className="mt-1 text-lg font-semibold text-foreground">
                            {rollout.instances_per_minute} instances/min
                          </p>
                          <p className="text-xs text-muted-foreground">
                            ({rollout.instances_per_minute * 60}/hour)
                          </p>
                        </div>
                        <div>
                          <p className="text-sm font-medium text-muted-foreground">Source Repo</p>
                          <p className="mt-1 text-lg font-semibold text-foreground font-mono">
                            {rollout.source_repo_name}
                          </p>
                        </div>
                        <div>
                          <p className="text-sm font-medium text-muted-foreground">Est. Duration</p>
                          <p className="mt-1 text-lg font-semibold text-foreground">
                            {rollout.estimated_hours} hours
                          </p>
                        </div>
                        <div>
                          <p className="text-sm font-medium text-muted-foreground">Effective Rate</p>
                          <p className="mt-1 text-lg font-semibold text-foreground">
                            {rollout.effective_rate ? `${rollout.effective_rate}/hour` : '-'}
                          </p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>

                  {/* Timeline Card */}
                  <Card className="shadow-none">
                    <CardHeader>
                      <CardTitle>Timeline</CardTitle>
                      <CardDescription>Rollout timestamps</CardDescription>
                    </CardHeader>
                    <CardContent>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <p className="text-sm font-medium text-muted-foreground">Created</p>
                          <p className="mt-1 text-sm text-foreground">
                            {formatDateTime(rollout.created_at)}
                          </p>
                        </div>
                        <div>
                          <p className="text-sm font-medium text-muted-foreground">Started</p>
                          <p className="mt-1 text-sm text-foreground">
                            {formatDateTime(rollout.started_at)}
                          </p>
                        </div>
                        {rollout.paused_at && (
                          <div>
                            <p className="text-sm font-medium text-muted-foreground">Paused</p>
                            <p className="mt-1 text-sm text-foreground">
                              {formatDateTime(rollout.paused_at)}
                            </p>
                          </div>
                        )}
                        {rollout.completed_at && (
                          <div>
                            <p className="text-sm font-medium text-muted-foreground">Completed</p>
                            <p className="mt-1 text-sm text-foreground">
                              {formatDateTime(rollout.completed_at)}
                            </p>
                          </div>
                        )}
                        {rollout.status === 'running' && rollout.estimated_completion_at && (
                          <div>
                            <p className="text-sm font-medium text-muted-foreground">Est. Completion</p>
                            <p className="mt-1 text-sm text-foreground">
                              {formatDateTime(rollout.estimated_completion_at)}
                            </p>
                          </div>
                        )}
                      </div>
                    </CardContent>
                  </Card>

                  {/* Error Log Card */}
                  {rollout.error_log && (
                    <Card className="shadow-none border-destructive/30 bg-destructive/5">
                      <CardHeader>
                        <CardTitle className="text-destructive">Error Log</CardTitle>
                        <CardDescription>Recent failures during rollout</CardDescription>
                      </CardHeader>
                      <CardContent>
                        <pre className="text-xs bg-destructive/10 border border-destructive/30 rounded p-3 overflow-x-auto max-h-60 overflow-y-auto font-mono whitespace-pre-wrap">
                          {rollout.error_log}
                        </pre>
                      </CardContent>
                    </Card>
                  )}
                </div>

                {/* Actions Sidebar */}
                <div className="space-y-6">
                  <Card className="shadow-none">
                    <CardHeader>
                      <CardTitle>Actions</CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-3">
                      {rollout.status === 'pending' && (
                        <Button
                          variant="default"
                          className="w-full justify-start bg-green-600 hover:bg-green-700"
                          onClick={handleStart}
                        >
                          Start Rollout
                        </Button>
                      )}
                      {rollout.status === 'running' && (
                        <Button
                          variant="outline"
                          className="w-full justify-start border-orange-300 text-orange-700 hover:bg-orange-50 dark:border-orange-700 dark:text-orange-400 dark:hover:bg-orange-950"
                          onClick={handlePause}
                        >
                          Pause Rollout
                        </Button>
                      )}
                      {rollout.status === 'paused' && (
                        <Button
                          variant="default"
                          className="w-full justify-start bg-green-600 hover:bg-green-700"
                          onClick={handleResume}
                        >
                          Resume Rollout
                        </Button>
                      )}
                      {(rollout.status === 'running' || rollout.status === 'paused' || rollout.status === 'pending') && (
                        <Button
                          variant="outline"
                          className="w-full justify-start text-destructive hover:text-destructive hover:bg-destructive/10"
                          onClick={handleCancel}
                        >
                          Cancel Rollout
                        </Button>
                      )}
                      {(rollout.status === 'completed' || rollout.status === 'cancelled') && (
                        <Button
                          variant="outline"
                          className="w-full justify-start text-destructive hover:text-destructive hover:bg-destructive/10"
                          onClick={handleDelete}
                        >
                          Delete Rollout
                        </Button>
                      )}
                    </CardContent>
                  </Card>

                  {/* Live Update Notice */}
                  {rollout.status === 'running' && (
                    <Card className="shadow-none border-blue-500/20 bg-blue-500/5">
                      <CardContent className="pt-6">
                        <div className="flex items-center gap-2 text-sm text-muted-foreground">
                          <div className="h-2 w-2 rounded-full bg-blue-500 animate-pulse"></div>
                          Auto-refreshing every 10 seconds
                        </div>
                      </CardContent>
                    </Card>
                  )}

                  {/* Quick Stats */}
                  <Card className="shadow-none">
                    <CardHeader>
                      <CardTitle>Quick Stats</CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      <div>
                        <p className="text-sm font-medium text-muted-foreground">Success Rate</p>
                        <p className="mt-1 text-lg font-semibold text-foreground">
                          {rollout.created_count + rollout.failed_count > 0
                            ? `${((rollout.created_count / (rollout.created_count + rollout.failed_count)) * 100).toFixed(1)}%`
                            : '-'}
                        </p>
                      </div>
                      <div>
                        <p className="text-sm font-medium text-muted-foreground">Completion</p>
                        <p className="mt-1 text-lg font-semibold text-foreground">
                          {rollout.progress_percentage}%
                        </p>
                      </div>
                    </CardContent>
                  </Card>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  )
}

Show.layout = (page: React.ReactNode) => <AdminLayout>{page}</AdminLayout>

export default Show
